import { memo, useCallback, useRef } from 'react'
import { setChatMinimized } from '../../../../../../actions/layout'
import { sendPrivateMessage } from '../../../../../../actions/sendPrivateMessage'
import { toggleFloaterVideo } from '../../../../../../app-state/modals/state.floater'
import { FloaterSize, RoomView } from '../../../../../../constants/enums'
import {
    closeWhiteboard,
    openWhiteboard,
} from '../../../../../../state/state.whiteboard'
import i18n from '../../../../../../utilities/i18n'
import {
    AppStateType,
    useAppStateSelector,
} from '../../../../../Hooks/useAppState'
import { useLayoutSelector } from '../../../../../Hooks/useLayout'
import { useRoomSelector } from '../../../../../Hooks/useRoom'
import { useUserSelector } from '../../../../../Hooks/useUser'
import { useWhiteboardSelector } from '../../../../../Hooks/useWhiteboard'
import FlexGrid from '../../../../../common/FlexGrid'
import { COMPONENT_HEIGHTS } from '../../../../../theme'
import ShareFreeAlbums from '../../../../ShareFreeAlbums'
import Whiteboard, { WhiteboardRoot } from '../../../../Whiteboard'
import ChatActionsBar from '../../../ActionsBar'
import ChatHeader from '../../../ChatHeader'
import Compose from '../../../Compose'
import { calcVideoDimensions } from '../../../common/utilities'
import Chat from '../../Chat'
import { ViewProps, getVerticalChatHeight } from '../common/helpers'
import {
    chatGridStyles,
    chatPaneStyles,
    shareAlbumsStyles,
    verticalPmStyles,
} from '../common/styles'

const BasicPM = (props: ViewProps) => {
    const {
        id,
        size,
        roomView,
        height,
        width,
        vertical,
        fullWindow,
        disableKeyboardOpenDetection,
        surrogateName,
    } = props
    const chatGridContainerRef = useRef<HTMLDivElement>(null)
    const chatRef = useRef(null)
    const isLounge = useRoomSelector(id, 'isLounge')
    const isWhiteboardOpen = useWhiteboardSelector('open')
    const isMobile = useAppStateSelector(AppStateType.APP, 'isMobile')

    const hasWhiteboard = useUserSelector(id, 'hasWhiteboard')
    const isModel = useUserSelector(id, 'isModel') || isLounge
    const isOffline = useUserSelector(id, 'isOffline')
    const isBroadcasting = useUserSelector(id, 'isBroadcasting')
    const name = useUserSelector(id, 'name')
    const shareFreeAlbums = useUserSelector(id, 'shareFreeAlbums')
    const slug = useUserSelector(id, 'slug')

    const modelName = !isLounge ? name : surrogateName

    const isPM = roomView === RoomView.PM

    const showVideo = isPM ? props.showVideo : true

    const minimized = useLayoutSelector('minimized')

    const { height: videoHeight = 0, width: videoWidth = 0 } =
        calcVideoDimensions(width, height, vertical, fullWindow)
    const chatWidth = vertical ? width : width - videoWidth

    const verticalChatHeight = getVerticalChatHeight({
        isMobile,
        height,
        videoHeight,
        isOffline,
        isBroadcasting,
        isPM,
        showVideo,
    })

    const onCloseWhiteboard = useCallback(() => {
        closeWhiteboard()
    }, [])
    const onToggleWhiteboard = useCallback(() => {
        if (isWhiteboardOpen) {
            closeWhiteboard()
        } else {
            openWhiteboard(modelName)
        }
    }, [isWhiteboardOpen, modelName])
    const scrollWhiteboard = useCallback(() => {
        chatGridContainerRef.current.scroll({
            top: vertical
                ? verticalChatHeight
                : height - COMPONENT_HEIGHTS.chatHeader,
            behavior: 'smooth',
        })
    }, [height, videoHeight, vertical, verticalChatHeight])

    const sendPm = useCallback((id: number, text: string) => {
        sendPrivateMessage(id, text)
        chatRef.current?.scrollToBottom?.()
    }, [])

    const onToggleStream = useCallback(() => {
        toggleFloaterVideo()
    }, [])

    const Header = (
        <ChatHeader
            id={id}
            preview={size < FloaterSize.Fullscreen}
            showBadge={isPM && !showVideo}
            pm={isPM}
            room
        />
    )

    const ActionsBar = (
        <ChatActionsBar
            id={id}
            roomView={roomView}
            fullWindow={fullWindow}
            onWhiteboard={onToggleWhiteboard}
            floating
            onVideoToggle={onToggleStream}
        />
    )

    const ChatInputBar = (
        <Compose
            hide={minimized}
            id={id}
            onSend={sendPm}
            setMinimized={setChatMinimized}
            placeholder={i18n.t(
                isPM ? 'chat.sendPrivateMessage' : 'chat.sendChatMessage',
            )}
            hideTipButton={!isModel}
            disableKeyboardOpenDetection={disableKeyboardOpenDetection}
            pm={isPM}
        />
    )

    return (
        <div css={verticalPmStyles}>
            {Header}
            {isBroadcasting && ActionsBar}
            <FlexGrid
                ref={chatGridContainerRef}
                overrides={chatGridStyles}
                style={{
                    overflowY:
                        isMobile || isWhiteboardOpen ? 'scroll' : 'hidden',
                }}
                data-testid="Chat__Section"
            >
                <div
                    css={chatPaneStyles}
                    style={{
                        height: verticalChatHeight,
                    }}
                    data-testid="Chat__Container"
                >
                    <Chat
                        id={id}
                        height={height}
                        onOpenWhiteboard={onToggleWhiteboard}
                        styleHeight="100%"
                        pm
                        chatRef={chatRef}
                    />
                    {ChatInputBar}
                </div>
                {/* [portrait] display any free mfc share albums below the chat panel (scroll the entire chat panel up) */}
                {shareFreeAlbums > 0 && !isWhiteboardOpen && (
                    <div css={shareAlbumsStyles}>
                        <ShareFreeAlbums
                            name={name}
                            width={chatWidth}
                            albums={shareFreeAlbums}
                        />
                    </div>
                )}
                {/* [portrait] whiteboard may be accessed by scrolling the entire chat panel up */}
                {hasWhiteboard && isWhiteboardOpen && (
                    <WhiteboardRoot>
                        <Whiteboard
                            identifier={slug}
                            showAvatar={false}
                            scrollIntoView={scrollWhiteboard}
                            onClose={onCloseWhiteboard}
                        />
                    </WhiteboardRoot>
                )}
            </FlexGrid>
        </div>
    )
}

export default memo(BasicPM)
